<template>
  <ModelInput
    v-if="resolvedColumnDefCache"
    :model-value="inputValue"
    :col="resolvedColumnDefCache"
    :model-name="modelName"
    :virtual-model-name="virtualModelName"
    :col-name="colName"
    :record="{}"
    @update:value-and-error="
      ({ value, error }) => {
        applyFilterLogic(value, error)
      }
    "
  />
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import { ColumnDef } from '../../../../../common/$models/ModelDef'
import { executeStringDefinedFunction } from '../../../../../common/utils'
import { registerComposableComponentSettings } from '../../../../ComposableComponents'
import { ComposableDataListService } from '../../ComposableDataListService'

const dataListFilterItemModelColumnInputProps = {
  filterColKey: { type: String, default: null },
  useExistingModelColumn: { type: Boolean, default: true },
  modelName: { type: String, default: null },
  virtualModelName: { type: String, default: null },
  colName: { type: String, default: null },
  filterOperator: { type: String, default: '_contains' },
  filterConditionKeyName: { type: String, default: null },
  columnDefOverrideFunctionString: { type: String, default: null },
  filterValueParserFunctionString: { type: String, default: null },
  filterValueUnparserFunctionString: { type: String, default: null },
}

/**
 * Registers the component with the ComposableComponentBuilder.
 */
registerComposableComponentSettings('DataListFilterItemModelColumnInput', {
  label: 'データリストモデル入力フィルター',
  category: 'DataList',
  shortDesc: '特定のカラムに対するフィルター入力を提供します。',
  configColumns: {
    filterColKey: {
      type: 'STRING',
      label: '検索対象のカラムキー',
      defaultValue: '',
      validate: { notEmpty: true },
      inputHelpText: `リレーション先を指定する場合は ドット接続 <code>parent_product.name</code> で指定します。`,
    },
    useExistingModelColumn: {
      type: 'BOOLEAN',
      label: '既存モデルのカラムを使用',
      defaultValue: true,
    },
    modelName: {
      type: 'STRING',
      label: 'モデル名',
      validate: { notEmpty: true },
      selections: () => {
        return $core.$modelsLoader.modelNameSelectOptions
      },
      enableIf: (record) => record.useExistingModelColumn,
      editCallback: ({ row, newValue, oldValue }) => {
        // カラム名をクリア
        if (newValue !== oldValue) {
          row.colName = null
          row.virtualModelName = null
        }
        return row
      },
    },
    virtualModelName: {
      type: 'STRING',
      label: '仮想モデル名',
      inputHelpText:
        'useExistingModelColumn が false の場合に、仮想的にモデル名を指定できます。カラム定義の参照には使用されません。',
      enableIf: (record) => record.useExistingModelColumn,
      selections(record) {
        return $core.$virtualModelsLoader.virtualModelNameSelectOptions
      },
      editCallback: ({ row, newValue, oldValue }) => {
        // カラム名をクリア
        if (newValue !== oldValue) {
          row.modelName = $core.$virtualModels[newValue]?.baseModel
          row.colName = null
        }
        return row
      },
    },
    colName: {
      type: 'STRING',
      label: 'カラム名',
      validate: { notEmpty: true },
      enableIf: (record) => record.useExistingModelColumn,
      dynamicSelections: true,
      selections: (record) => {
        if (record.useExistingModelColumn && record.modelName) {
          const targetColumns = record.virtualModelName
            ? $core.$virtualModels[record.virtualModelName]?.columnsMergedWithBaseModel
            : $core.$models[record.modelName]?.columns
          return Object.keys(targetColumns).map((colName) => ({
            value: colName,
            label: targetColumns[colName].label
              ? `${targetColumns[colName].label} (${colName})`
              : colName,
          }))
        }
        return []
      },
    },
    filterOperator: {
      type: 'STRING',
      label: 'フィルター演算子',
      defaultValue: '_contains',
      inputHelpText: `選択肢以外のフィルター条件を利用する場合は、直接入力してください。`,
      selections: () => [
        // 比較演算子
        { value: '_eq', label: '等しい (_eq)' },
        { value: '_neq', label: '等しくない (_neq)' },
        { value: '_gt', label: 'より大きい (_gt)' },
        { value: '_gte', label: '以上 (_gte)' },
        { value: '_lt', label: 'より小さい (_lt)' },
        { value: '_lte', label: '以下 (_lte)' },
        // 文字列演算子
        { value: '_contains', label: '含む (_contains)' },
        { value: '_ncontains', label: '含まない (_ncontains)' },
        { value: '_starts_with', label: '...で始まる (_starts_with)' },
        { value: '_nstarts_with', label: '...で始まらない (_nstarts_with)' },
        { value: '_ends_with', label: '...で終わる (_ends_with)' },
        { value: '_nends_with', label: '...で終わらない (_nends_with)' },
        // 配列演算子
        { value: '_in', label: 'いずれか (_in)' },
        { value: '_nin', label: 'いずれでもない (_nin)' },
        { value: '_between', label: '範囲内 (_between)' },
        { value: '_nbetween', label: '範囲外 (_nbetween)' },
        // Null/空演算子
        { value: '_null', label: 'NULL (_null)' },
        { value: '_nnull', label: 'NOT NULL (_nnull)' },
        { value: '_empty', label: '空 (_empty)' },
        { value: '_nempty', label: '空でない (_nempty)' },
      ],
    },
    filterConditionKeyName: {
      type: 'STRING',
      label: 'フィルターコンディションキー名',
      defaultValue: '',
      inputHelpText: `複数のフィルターコンポーネントを利用する場合に、同じキー名を利用すると、フィルターの状態を共有 (参照, 更新) することが可能です。指定しない場合は、ランダム生成されたユニークなキー名が設定されます。`,
    },
    columnDefOverrideFunctionString: {
      type: 'TEXT',
      label: 'カラム定義上書き関数',
      inputComponent: 'CodeEditor',
      inputHelpText:
        '引数 {vueComponentInstance, props, ComposableDataListServiceInstance} を受け取り、カラム定義オブジェクトを返す関数を文字列で指定します。',
      inputAttrs: {
        initCode: (callerVueInstance) => {
          return `/**
 * @param {Object} vueComponentInstance - このコンポーネントの Vue インスタンス
 * @param {{modelName: string, virtualModelName: string, colName: string, filterOperator: string}} props - コンポーネントプロパティ
 * @param {ComposableDataListService} ComposableDataListServiceInstance - ComposableDataListServiceインスタンス
 */
return async ({vueComponentInstance, props, ComposableDataListServiceInstance}) => {
  /** @type {ColumnDefPartial<any>} */
  const columnDefOverrides = {}
  return columnDefOverrides
}`
        },
      },
    },
    filterValueParserFunctionString: {
      type: 'TEXT',
      label: 'フィルター値パース関数（入力→フィルタ）',
      inputComponent: 'CodeEditor',
      inputHelpText:
        '引数 {value, vueComponentInstance, props, ComposableDataListServiceInstance} を受け取り、フィルタリングに使用する値を返す関数を文字列で指定します。入力値をフィルタ条件に変換します。',
      inputAttrs: {
        initCode: (callerVueInstance) => {
          return `/**
 * @param {any} value - 入力された値
 * @param {Object} vueComponentInstance - このコンポーネントの Vue インスタンス
 * @param {{modelName: string, virtualModelName: string, colName: string, filterOperator: string}} props - コンポーネントプロパティ
 * @param {ComposableDataListService} ComposableDataListServiceInstance - ComposableDataListServiceインスタンス
 */
return ({value, vueComponentInstance, props, ComposableDataListServiceInstance}) => {
  // 例: 日付文字列をタイムスタンプに変換
  // if (value && typeof value === 'string') {
  //   return new Date(value).getTime()
  // }
  return value
}`
        },
      },
    },
    filterValueUnparserFunctionString: {
      type: 'TEXT',
      label: 'フィルター値逆パース関数（フィルタ→入力）',
      inputComponent: 'CodeEditor',
      inputHelpText:
        '引数 {value, vueComponentInstance, props, ComposableDataListServiceInstance} を受け取り、フィルタ値を入力値に変換する関数を文字列で指定します。初期値検出時に使用されます。',
      inputAttrs: {
        initCode: (callerVueInstance) => {
          return `/**
 * @param {any} value - フィルタから取得した値
 * @param {Object} vueComponentInstance - このコンポーネントの Vue インスタンス
 * @param {{modelName: string, virtualModelName: string, colName: string, filterOperator: string}} props - コンポーネントプロパティ
 * @param {ComposableDataListService} ComposableDataListServiceInstance - ComposableDataListServiceインスタンス
 */
return ({value, vueComponentInstance, props, ComposableDataListServiceInstance}) => {
  // 例: タイムスタンプを日付文字列に変換
  // if (value && typeof value === 'number') {
  //   return new Date(value).toISOString().split('T')[0]
  // }
  return value
}`
        },
      },
    },
  },
})

export default defineComponent({
  name: 'DataListFilterItemModelColumnInput',
  props: dataListFilterItemModelColumnInputProps,
  setup() {
    return {
      dls: inject<ComposableDataListService>(
        'ComposableDataListServiceInstance',
      ) as ComposableDataListService,
    }
  },
  data() {
    return {
      inputValue: null,
      resolvedColumnDefCache: null as ColumnDef | null,
      overrideColumnDefFunctionExecutable: null as Function | null,
      overrideColumnDef: null as Partial<ColumnDef> | null,
      filterValueParserFunctionExecutable: null as Function | null,
      filterValueUnparserFunctionExecutable: null as Function | null,
    }
  },
  computed: {
    filterKey(): string {
      return this.filterConditionKeyName || `dls-filter-item-model-input--${this._.uid}`
    },
    baseColDef(): ColumnDef | null {
      if (this.useExistingModelColumn && this.modelName && this.colName) {
        return this.virtualModelName
          ? $core.$virtualModels[this.virtualModelName].columnsMergedWithBaseModel[
              this.colName
            ]
          : $core.$models[this.modelName].columns[this.colName]
      }
      return null
    },
  },
  async created() {
    this.prepareAndInit()
  },
  methods: {
    async prepareAndInit() {
      await this.prepareColumnDefOverrideFunction()
      await this.prepareParserFunctions()
      await this.resolveColumnDef()
      await this.initializeInputValue()
    },
    async resolveColumnDef() {
      if (typeof this.overrideColumnDefFunctionExecutable === 'function') {
        const overrideColumnDef = await this.overrideColumnDefFunctionExecutable({
          vueComponentInstance: this,
          props: this.$props,
          ComposableDataListServiceInstance: this.dls,
        })
        if (overrideColumnDef && !overrideColumnDef.type && !this.baseColDef?.type) {
          // 型が不明な場合は文字列型として扱う
          overrideColumnDef.type = 'STRING'
        }
        this.overrideColumnDef = overrideColumnDef
      } else {
        this.overrideColumnDef = null
      }
      this.resolvedColumnDefCache = {
        ...this.baseColDef,
        ...(this.overrideColumnDef || {}),
      }
    },
    async prepareColumnDefOverrideFunction() {
      if (this.columnDefOverrideFunctionString) {
        try {
          this.overrideColumnDefFunctionExecutable = await executeStringDefinedFunction({
            functionString: this.columnDefOverrideFunctionString,
            functionArgValues: {
              vueComponentInstance: this,
              props: this.$props,
              ComposableDataListServiceInstance: this.dls,
            },
            errorThrow: true,
          })
        } catch (error) {
          console.error(
            '[prepareColumnDefOverrideFunction] Error preparing column def override function:',
            error,
          )
          this.overrideColumnDefFunctionExecutable = null
        }
      }
    },
    async prepareParserFunctions() {
      if (this.filterValueParserFunctionString) {
        try {
          this.filterValueParserFunctionExecutable = await executeStringDefinedFunction({
            functionString: this.filterValueParserFunctionString,
            functionArgValues: {
              vueComponentInstance: this,
              props: this.$props,
              ComposableDataListServiceInstance: this.dls,
            },
            errorThrow: true,
          })
        } catch (error) {
          console.error(
            '[prepareParserFunctions] Error preparing parser function:',
            error,
          )
          this.filterValueParserFunctionExecutable = null
        }
      }

      if (this.filterValueUnparserFunctionString) {
        try {
          this.filterValueUnparserFunctionExecutable = await executeStringDefinedFunction({
            functionString: this.filterValueUnparserFunctionString,
            functionArgValues: {
              vueComponentInstance: this,
              props: this.$props,
              ComposableDataListServiceInstance: this.dls,
            },
            errorThrow: true,
          })
        } catch (error) {
          console.error(
            '[prepareParserFunctions] Error preparing unparser function:',
            error,
          )
          this.filterValueUnparserFunctionExecutable = null
        }
      }
    },
    extractValueFromFilter(filterObject: any): any {
      if (!filterObject || typeof filterObject !== 'object') {
        return undefined
      }

      const pathSegments = this.filterColKey.split('.')
      let current = filterObject

      for (const segment of pathSegments) {
        if (!current || typeof current !== 'object') {
          return undefined
        }
        current = current[segment]
      }

      if (current && typeof current === 'object') {
        return current[this.filterOperator]
      }

      return undefined
    },
    async initializeInputValue() {
      if (!this.filterConditionKeyName) {
        return
      }

      const existingFilter = this.dls.query.filtersByKey?.[this.filterConditionKeyName]
      if (!existingFilter || Object.keys(existingFilter).length === 0) {
        return
      }

      let value = this.extractValueFromFilter(existingFilter)

      if (value !== undefined && typeof this.filterValueUnparserFunctionExecutable === 'function') {
        try {
          value = await this.filterValueUnparserFunctionExecutable({
            value,
            vueComponentInstance: this,
            props: this.$props,
            ComposableDataListServiceInstance: this.dls,
          })
        } catch (error) {
          console.error('[initializeInputValue] Error applying unparser function:', error)
        }
      }

      if (value !== undefined) {
        this.inputValue = value
      }
    },
    async applyFilterLogic(value: any, error: any) {
      if (!this.filterColKey) {
        return
      }
      if (this.inputValue === value) {
        return
      }
      this.inputValue = value
      if (
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      ) {
        this.clearFilter()
        return
      }

      let parsedValue = value
      if (typeof this.filterValueParserFunctionExecutable === 'function') {
        try {
          parsedValue = await this.filterValueParserFunctionExecutable({
            value,
            vueComponentInstance: this,
            props: this.$props,
            ComposableDataListServiceInstance: this.dls,
          })
        } catch (error) {
          console.error('[applyFilterLogic] Error applying parser function:', error)
          parsedValue = value
        }
      }

      const filterObject = await this.generateFilterObject(parsedValue)
      this.dls.query.applyFilter(this.filterKey, filterObject)
    },
    async generateFilterObject(value: any = this.inputValue) {
      const dotSplittedColKey = this.filterColKey.split('.')
      const filterObject = dotSplittedColKey.reverse().reduce((acc, colKey, index) => {
        return {
          [colKey]: index === 0 ? {
            [this.filterOperator]: value,
          } : acc,
        }
      }, {})
      return filterObject
    },
    clearFilter() {
      this.dls.query.applyFilter(this.filterKey, {})
    },
  },
})
</script>
